'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { SEARCH_PARAMS } from '../constants/searchParams';
import { locationService } from '../services/location.service';
import { type MapBounds } from '../utils/mapUtils';
import { DEFAULT_LAT, DEFAULT_LNG, DEFAULT_ZOOM, parseCoord } from '../utils/searchUtils';
import { useCurrentPosition } from './useCurrentPosition';
import { useSyncSearchParams } from './useSyncSearchParams';

const REVERSE_GEOCODE_DEBOUNCE_MS = 500;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UseSearchPositionReturn {
  center: { lat: number; lng: number } | null;
  zoom: number;
  panVersion: number;
  mapBounds: MapBounds | null;
  mapReady: boolean;
  setLocation: (location: { latitude: number; longitude: number; name: string }) => void;
  handleMapIdle: (
    bounds: { neLat: number; neLng: number; swLat: number; swLng: number },
    zoom: number | undefined
  ) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSearchPosition(): UseSearchPositionReturn {
  const mapReadyRef = useRef(false);
  const { get, replaceAll } = useSyncSearchParams();

  const urlLat = parseCoord(get(SEARCH_PARAMS.LATITUDE), -90, 90);
  const urlLng = parseCoord(get(SEARCH_PARAMS.LONGITUDE), -180, 180);
  const hasUrlPosition = urlLat !== null && urlLng !== null;

  // Read from localStorage once on render — only when there's no URL position
  const lastStoredLocation = !hasUrlPosition ? locationService.getLastSearchLocation() : null;
  // Stable ref so effects can read it without causing re-runs
  const lastStoredLocationRef = useRef(lastStoredLocation);

  // ── Map state ─────────────────────────────────────────────────────────
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const [zoom, setZoom] = useState<number>(() => parseCoord(get(SEARCH_PARAMS.ZOOM), 1, 20) ?? DEFAULT_ZOOM);
  const [panVersion, setPanVersion] = useState(0);

  const [commandedCenter, setCommandedCenter] = useState<{ lat: number; lng: number } | null>(() => {
    if (hasUrlPosition) return { lat: urlLat, lng: urlLng };
    if (lastStoredLocation) return { lat: lastStoredLocation.latitude, lng: lastStoredLocation.longitude };
    return { lat: DEFAULT_LAT, lng: DEFAULT_LNG };
  });

  // Track reverse-geocode requests so stale responses from rapid panning are ignored
  const reverseGeocodeRequestIdRef = useRef(0);
  const reverseGeocodeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Suppresses the next reverse-geocode cycle — used when the user explicitly picks a
  // location from the modal, so the picked name isn't overwritten by the map's idle event.
  const skipNextReverseGeocodeRef = useRef(false);
  // If the URL already carried a display name on mount (e.g. user came from the home
  // search bar or localStorage restore), preserve it through the map's initial idle.
  const hadInitialDisplayNameRef = useRef(!!get(SEARCH_PARAMS.DISPLAY_NAME) || !!lastStoredLocation?.name);

  // ── Sync localStorage with URL on mount ──────────────────────────────
  useEffect(() => {
    if (hasUrlPosition) {
      // URL has position (came from home search or direct link) → persist it
      locationService.saveLastSearchLocation({
        latitude: urlLat!,
        longitude: urlLng!,
        name: get(SEARCH_PARAMS.DISPLAY_NAME) ?? ''
      });
    } else if (lastStoredLocationRef.current) {
      // No URL position but we have a stored one → inject it so the label and map are restored
      const stored = lastStoredLocationRef.current;
      replaceAll((params) => {
        params.set(SEARCH_PARAMS.LATITUDE, stored.latitude.toString());
        params.set(SEARCH_PARAMS.LONGITUDE, stored.longitude.toString());
        params.set(SEARCH_PARAMS.DISPLAY_NAME, stored.name);
        params.set(SEARCH_PARAMS.NAMED_LAT, stored.latitude.toString());
        params.set(SEARCH_PARAMS.NAMED_LNG, stored.longitude.toString());
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Geolocation override ─────────────────────────────────────────────
  const { data: currentPosition } = useCurrentPosition();

  useEffect(() => {
    if (hasUrlPosition) return;
    // Don't override with geolocation when we have a stored location to restore
    if (lastStoredLocationRef.current) return;
    setCommandedCenter({
      lat: currentPosition?.latitude ?? DEFAULT_LAT,
      lng: currentPosition?.longitude ?? DEFAULT_LNG
    });
  }, [currentPosition, hasUrlPosition]);

  useEffect(() => {
    return () => {
      if (reverseGeocodeTimeoutRef.current) clearTimeout(reverseGeocodeTimeoutRef.current);
    };
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────

  const setLocation = useCallback(
    (location: { latitude: number; longitude: number; name: string }) => {
      setCommandedCenter({ lat: location.latitude, lng: location.longitude });
      setPanVersion((v) => v + 1);
      skipNextReverseGeocodeRef.current = true;

      locationService.saveLastSearchLocation(location);

      replaceAll((params) => {
        params.set(SEARCH_PARAMS.LATITUDE, location.latitude.toString());
        params.set(SEARCH_PARAMS.LONGITUDE, location.longitude.toString());
        params.set(SEARCH_PARAMS.DISPLAY_NAME, location.name);
        params.set(SEARCH_PARAMS.NAMED_LAT, location.latitude.toString());
        params.set(SEARCH_PARAMS.NAMED_LNG, location.longitude.toString());
      });
    },
    [replaceAll]
  );

  const handleMapIdle = useCallback(
    (bounds: { neLat: number; neLng: number; swLat: number; swLng: number }, newZoom: number | undefined) => {
      const isFirstIdle = !mapReadyRef.current;
      if (isFirstIdle) {
        mapReadyRef.current = true;
        setMapReady(true);
      }
      setMapBounds(bounds);

      const center = {
        lat: (bounds.neLat + bounds.swLat) / 2,
        lng: (bounds.neLng + bounds.swLng) / 2
      };

      replaceAll((params) => {
        params.set(SEARCH_PARAMS.LATITUDE, String(center.lat));
        params.set(SEARCH_PARAMS.LONGITUDE, String(center.lng));
        if (newZoom !== undefined && newZoom >= 1) {
          params.set(SEARCH_PARAMS.ZOOM, String(newZoom));
          setZoom(newZoom);
        } else {
          params.delete(SEARCH_PARAMS.ZOOM);
        }
      });

      // Debounced reverse-geocode so the display name tracks the map center
      if (skipNextReverseGeocodeRef.current) {
        skipNextReverseGeocodeRef.current = false;
        return;
      }
      // Preserve URL-hydrated display name on the map's very first settle
      if (isFirstIdle && hadInitialDisplayNameRef.current) {
        hadInitialDisplayNameRef.current = false;
        return;
      }
      if (reverseGeocodeTimeoutRef.current) clearTimeout(reverseGeocodeTimeoutRef.current);
      const requestId = ++reverseGeocodeRequestIdRef.current;
      reverseGeocodeTimeoutRef.current = setTimeout(async () => {
        const result = await locationService.reverseGeocode(center.lat, center.lng);
        if (requestId !== reverseGeocodeRequestIdRef.current) return;
        if (!result) return;
        replaceAll((params) => {
          params.set(SEARCH_PARAMS.DISPLAY_NAME, result.name);
          params.set(SEARCH_PARAMS.NAMED_LAT, String(center.lat));
          params.set(SEARCH_PARAMS.NAMED_LNG, String(center.lng));
        });
        locationService.saveLastSearchLocation({
          latitude: center.lat,
          longitude: center.lng,
          name: result.name
        });
      }, REVERSE_GEOCODE_DEBOUNCE_MS);
    },
    [replaceAll]
  );

  return {
    center: commandedCenter,
    zoom,
    panVersion,
    mapBounds,
    mapReady,
    setLocation,
    handleMapIdle
  };
}
